// src/pages/FloorplanUtilisation/hooks/useFloorplanData.js

import { useState, useEffect, useMemo, useContext  } from "react";
import { functions } from "../../../constants/common-functions";
import {EMPTY_FILTER_BEAN} from "../../../constants/filter-constants";
import {store} from '../../../context/store';

/* ------------------------------------------------------------- */
/* Build overall intensity map                                   */
/* ------------------------------------------------------------- */
function buildIntensity(records) {

  const counts = {};
  let max = 1;

  records.forEach(function (r) {

    if (!r || !r.spaceId) {
      return;
    }

    const add = r.occupiedFlag === 1 ? 1 : 0;

    counts[r.spaceId] = (counts[r.spaceId] || 0) + add;

    if (counts[r.spaceId] > max) {
      max = counts[r.spaceId];
    }
  });

  const intensities = {};

  Object.keys(counts).forEach(function (k) {
    intensities[k] = counts[k] / max;
  });

  return intensities;
}

/* ------------------------------------------------------------- */
/* Main hook                                                     */
/* ------------------------------------------------------------- */
export default function useFloorplanData(floorId, organisationIds) {

  const [data, setData] = useState([]);
  const [intensityMap, setIntensityMap] = useState({});
  const [timelineFrames, setTimelineFrames] = useState([]);
  const [loading, setLoading] = useState(false);

  const [activities, setActivities] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [activityLookup, setActivityLookup] = useState({});
  
  const globalState = useContext(store);
  
  const filterBean = {};
  		
  	const queryContext =
  		    globalState?.state?.queryContext;


  /* ----------------------------------------------------------- */
  /* 🔹 STEP 1 — LOAD ACTIVITY DEFINITIONS FIRST                 */
  /* ----------------------------------------------------------- */
  useEffect(function () {

    functions.getArrayFromAjax(

      "/resolutionsoftwareplatform/utilisation/activity-types-used",

      function (results) {

        const map = {};

        results.forEach(function (a) {

          map[a.id] = {
            id: a.id,
            name: a.name,
            color: a.color || "#999"
          };

        });

        setActivityLookup(map);

      },

      function (err) {
        console.log("Error loading activity defs:", err);
      }

    );

  }, []);

  /* ----------------------------------------------------------- */
  /* 🔹 STEP 2 — LOAD OBSERVATIONS (AFTER ACTIVITIES READY)       */
  /* ----------------------------------------------------------- */
  useEffect(function () {
	
    if (!floorId) return;

    // 🔥 IMPORTANT — wait for activities
    if (!activityLookup || Object.keys(activityLookup).length === 0) return;

	let url =
	    "/resolutionsoftwareplatform/dashboard/utilisation/floorplan/observations"
	    + "?floorId=" + floorId;

	if (organisationIds && organisationIds.length > 0) {

	    organisationIds.forEach(function (id) {

	        url += "&organisationIds=" + id;
	    });
	}

    setLoading(true);

    functions.postFilterBeanAjax(

      url,
	  functions.buildPostBodyFilterBeanQueryContext(filterBean, queryContext),
      function (rows) {

        rows = rows || [];
        setData(rows);

        /* ---------- Intensity ---------- */
        setIntensityMap(buildIntensity(rows));

        /* ---------- Time buckets ---------- */
        const buckets = {};
        const activityMap = new Map();
		const organisationMap = new Map();

        rows.forEach(function (r) {

          if (!r || !r.spaceId) return;
		  
		  
		  if (r.organisationId != null) {

		      organisationMap.set(
		          r.organisationId,
		          {
		              id: r.organisationId,
		              name: r.organisationName || "Unknown",
		              color: r.organisationColor || "#999"
		          }
		      );
		  }

          const key = r.observationDate + "_" + r.observationHour;

          if (!buckets[key]) {
            buckets[key] = [];
          }

          buckets[key].push(r);

          /* ---------- Activity lookup from DB ---------- */
          if (r.activityId != null) {

            const def = activityLookup[r.activityId];

            activityMap.set(
              r.activityId,
              {
                id: r.activityId,
                name: def ? def.name : "Unknown",
                color: def ? def.color : "#999"
              }
            );
          }
        });

        /* ---------- Timeline frames ---------- */
		const frameKeys =
		    Object.keys(buckets).sort(
		        function (a, b) {

		            const aParts =
		                a.split("_");

		            const bParts =
		                b.split("_");

		            const aTs =
		                new Date(
		                    aParts[0]
		                    + "T"
		                    + (
		                        aParts[1].padStart(2, "0")
		                    )
		                    + ":00:00"
		                ).getTime();

		            const bTs =
		                new Date(
		                    bParts[0]
		                    + "T"
		                    + (
		                        bParts[1].padStart(2, "0")
		                    )
		                    + ":00:00"
		                ).getTime();

		            return aTs - bTs;
		        }
		    );

		const frames = frameKeys.map(function (key, index) {

		  const occupancyMap = {};

		  const parts = key.split("_");
		  const date = parts[0];
		  const hour = parseInt(parts[1]);

		  buckets[key].forEach(function (r) {

		    occupancyMap[r.spaceId] = {
		      occupiedFlag: r.occupiedFlag,
		      signsOfLifeFlag: r.signsOfLifeFlag,
		      activityId: r.activityId != null ? r.activityId : null
		    };
		  });

		  // 🔥 BUILD NICE LABELS
		  const startHour = hour;
		  const endHour = hour + 1;

		  const startTime = (startHour < 10 ? "0" : "") + startHour + ":00";
		  const endTime = (endHour < 10 ? "0" : "") + endHour + ":00";

		  return {
		    key: key,
		    index: index,
		    occupancyMap: occupancyMap,

		    // 👇 NEW FIELDS
		    date: date,
		    startTime: startTime,
		    endTime: endTime
		  };
		});

        setTimelineFrames(frames);
        setActivities(Array.from(activityMap.values()));
		setOrganisations(Array.from(organisationMap.values()));
        setLoading(false);
      },

      function (err) {
        console.log("Error loading floorplan data:", err);
        setLoading(false);
      }

    );

  }, [queryContext?.utilisationSurveyId, floorId, activityLookup, organisationIds]);

  /* ----------------------------------------------------------- */
  /* Extract unique desks                                        */
  /* ----------------------------------------------------------- */
  const desks = useMemo(function () {

    const out = [];
    const seen = {};

    data.forEach(function (r) {

      if (!r || !r.spaceId || !r.svg) {
        return;
      }

      const key = String(r.spaceId).trim();

      if (!seen[key]) {
        seen[key] = true;

        out.push({
          spaceId: key,
          spaceCode: r.spaceCode,
          svg: r.svg,
		  organisationId: r.organisationId,
	      organisationName: r.organisationName,
	      organisationColor: r.organisationColor
        });
      }
    });
	console.log(out);
    return out;

  }, [data]);

  /* ----------------------------------------------------------- */
  /* Return                                                      */
  /* ----------------------------------------------------------- */
  return {
    desks: desks,
    data: data,
    loading: loading,
    intensityMap: intensityMap,
    timelineFrames: timelineFrames,
    activities: activities,
	organisations: organisations,
    refreshData: function () {}
  };
}